/**
 * Clinic API Endpoints
 * Handles clinic-related API calls
 */

import { axiosInstance } from '../axios.config';
import { CLINIC_ENDPOINTS } from '../../constants/api.constants';
import type {
  ClinicProfile,
  ClinicOnboardingData,
  ClinicStatus,
  ClinicDoctorAssociation,
  ClinicStaffAssociation,
  ClinicStats,
  ClinicActivity,
  ClinicDoctorStatus,
  ClinicDoctorRole,
  ClinicStaffStatus,
} from '@/types/clinic.types';
import { uploadDocument } from '../../utils/documentUpload.utils';

/**
 * Clinic API
 */
export const clinicApi = {
  /**
   * Get current authenticated user's clinic
   */
  getMe: async (): Promise<ClinicProfile> => {
    const response = await axiosInstance.get<ClinicProfile>(CLINIC_ENDPOINTS.GET_ME);
    return response.data;
  },

  /**
   * Get clinic by ID
   */
  getById: async (id: string): Promise<ClinicProfile> => {
    const endpoint = CLINIC_ENDPOINTS.GET_BY_ID.replace('{id}', id);
    const response = await axiosInstance.get<ClinicProfile>(endpoint);
    return response.data;
  },

  /**
   * Update clinic status
   */
  updateStatus: async (status: ClinicStatus): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(CLINIC_ENDPOINTS.UPDATE_STATUS, {
      status,
    });
    return response.data;
  },

  // Onboarding Steps

  /**
   * Step 1: Update clinic information
   */
  updateClinicInfo: async (data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_CLINIC_INFO,
      data
    );
    return response.data;
  },

  /**
   * Step 2: Update primary address
   */
  updatePrimaryAddress: async (data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_PRIMARY_ADDRESS,
      data
    );
    return response.data;
  },

  /**
   * Step 3: Update contact person
   */
  updateContactPerson: async (data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_CONTACT_PERSON,
      data
    );
    return response.data;
  },

  /**
   * Step 4: Update licensing information
   */
  updateLicensing: async (data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_LICENSING,
      data
    );
    return response.data;
  },

  /**
   * Step 5: Update services provided
   */
  updateServicesProvided: async (data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_SERVICES_PROVIDED,
      data
    );
    return response.data;
  },

  /**
   * Step 6: Update operations schedule
   */
  updateOperations: async (data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_OPERATIONS,
      data
    );
    return response.data;
  },

  /**
   * Step 7: Update insurance billing
   */
  updateInsuranceBilling: async (data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_INSURANCE_BILLING,
      data
    );
    return response.data;
  },

  /**
   * Upload a single file and get metadata
   * Uses documentUpload utility to upload to media service
   */
  uploadFile: async (file: File, type?: string): Promise<{
    fileName: string;
    bucket: string;
    key: string;
    baseUrl: string;
    cdnUrl: string;
    contentType: string;
    fileSize: number;
    s3Url: string;
  }> => {
    // Upload file using documentUpload utility
    const folderPath = type ? `clinic/files/${type}` : 'clinic/files';
    const uploadResult = await uploadDocument(file, { folderPath });
    
    // Return in the same format as before for compatibility
    return {
      fileName: uploadResult.fileName,
      bucket: uploadResult.bucket,
      key: uploadResult.key,
      baseUrl: uploadResult.baseUrl,
      cdnUrl: uploadResult.cdnUrl,
      contentType: uploadResult.contentType,
      fileSize: uploadResult.fileSize,
      s3Url: uploadResult.s3Url,
    };
  },

  /**
   * Step 8: Upload documents (sends metadata JSON)
   */
  uploadDocuments: async (data: {
    logo?: {
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    };
    businessLicense?: {
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    };
    w9Form?: {
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    };
    cliaCertificate?: {
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    };
    addressProof?: {
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    };
    optionalCertificates?: Array<{
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    }>;
    facilityPhotos?: Array<{
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    }>;
  }): Promise<ClinicProfile> => {
    console.log('=== API Service - uploadDocuments called ===');
    console.log('data:', data);
    console.log('data type:', typeof data);
    console.log('data is null?', data === null);
    console.log('data is undefined?', data === undefined);
    console.log('data keys:', Object.keys(data || {}));
    console.log('data JSON:', JSON.stringify(data, null, 2));
    console.log('Endpoint:', CLINIC_ENDPOINTS.ONBOARDING_DOCUMENTS_UPLOAD);
    
    // Critical check - error if data is empty!
    if (!data || Object.keys(data).length === 0) {
      console.error('🚨 CRITICAL ERROR: Data is EMPTY in API call!');
      console.error('The request body will be empty {}');
      alert('API ERROR: Empty data being sent! Check console logs.');
      throw new Error('Cannot upload documents: data is empty');
    }
    
    console.log('=== Making PUT request ===');
    
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_DOCUMENTS_UPLOAD,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    console.log('=== API Service - Response received ===');
    console.log('Response data:', response.data);
    return response.data;
  },

  /**
   * Step 9: Update terms and compliance
   */
  updateTermsCompliance: async (data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_TERMS_COMPLIANCE,
      data
    );
    return response.data;
  },

  /**
   * Step 10: Review and submit
   */
  reviewSubmit: async (data?: Partial<ClinicOnboardingData>): Promise<ClinicProfile> => {
    const response = await axiosInstance.put<ClinicProfile>(
      CLINIC_ENDPOINTS.ONBOARDING_REVIEW_SUBMIT,
      data || {}
    );
    return response.data;
  },

  // Additional clinic management endpoints

  /**
   * Get clinic statistics
   */
  getStats: async (clinicId?: string): Promise<ClinicStats> => {
    const endpoint = clinicId ? `/clinic/${clinicId}/stats` : '/clinic/stats';
    const response = await axiosInstance.get<ClinicStats>(endpoint);
    return response.data;
  },

  /**
   * Get all doctors associated with clinic
   */
  getDoctors: async (params?: {
    status?: ClinicDoctorStatus;
    role?: ClinicDoctorRole;
    page?: number;
    limit?: number;
  }): Promise<{ doctors: ClinicDoctorAssociation[]; total: number }> => {
    const response = await axiosInstance.get<{ doctors: ClinicDoctorAssociation[]; total: number }>(
      '/clinic/doctors',
      { params }
    );
    return response.data;
  },

  /**
   * Get single doctor details
   */
  getDoctor: async (doctorId: string): Promise<ClinicDoctorAssociation> => {
    const response = await axiosInstance.get<ClinicDoctorAssociation>(`/clinic/doctors/${doctorId}`);
    return response.data;
  },

  /**
   * Invite doctor to clinic
   */
  inviteDoctor: async (data: {
    email: string;
    role?: ClinicDoctorRole;
    message?: string;
  }): Promise<{ message: string; invitationId: string }> => {
    const response = await axiosInstance.post<{ message: string; invitationId: string }>(
      '/clinic/doctors/invite',
      { doctorEmail: data.email, role: data.role, message: data.message }
    );
    return response.data;
  },

  /**
   * Approve doctor request to join clinic
   */
  approveDoctor: async (doctorId: string, role?: ClinicDoctorRole): Promise<ClinicDoctorAssociation> => {
    const response = await axiosInstance.post<ClinicDoctorAssociation>(
      `/clinic/doctors/${doctorId}/approve`,
      { role }
    );
    return response.data;
  },

  /**
   * Reject doctor request to join clinic
   */
  rejectDoctor: async (doctorId: string, reason?: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(
      `/clinic/doctors/${doctorId}/reject`,
      { reason }
    );
    return response.data;
  },

  /**
   * Update doctor role or status
   */
  updateDoctor: async (
    doctorId: string,
    data: {
      role?: ClinicDoctorRole;
      status?: ClinicDoctorStatus;
    }
  ): Promise<ClinicDoctorAssociation> => {
    const response = await axiosInstance.put<ClinicDoctorAssociation>(
      `/clinic/doctors/${doctorId}`,
      data
    );
    return response.data;
  },

  /**
   * Remove doctor from clinic
   */
  removeDoctor: async (doctorId: string, reason?: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete<{ message: string }>(`/clinic/doctors/${doctorId}`, {
      data: { reason },
    });
    return response.data;
  },

  /**
   * Get clinic activities/activity log
   */
  getActivities: async (params?: {
    type?: string;
    doctorId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ activities: ClinicActivity[]; total: number }> => {
    const response = await axiosInstance.get<{ activities: ClinicActivity[]; total: number }>(
      '/clinic/activities',
      { params }
    );
    return response.data;
  },

  /**
   * Request to join clinic (for doctors)
   */
  requestToJoin: async (clinicId: string, message?: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(`/clinic/${clinicId}/request-join`, {
      message,
    });
    return response.data;
  },

  /**
   * Get all staff members associated with clinic
   * GET /staff
   */
  getStaff: async (params?: {
    status?: 'ACTIVE' | 'INACTIVE';
    role?: 'DOCTOR' | 'NURSE' | 'RECEPTIONIST';
    page?: number;
    limit?: number;
  }): Promise<{
    staff: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await axiosInstance.get<{
      staff: any[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>('/staff', { params });
    return response.data;
  },

  /**
   * Get single staff member details
   */
  getStaffMember: async (userId: string): Promise<ClinicStaffAssociation> => {
    const response = await axiosInstance.get<ClinicStaffAssociation>(`/clinic/staff/${userId}`);
    return response.data;
  },

  /**
   * Invite staff member to clinic
   */
  inviteStaff: async (data: {
    email: string;
    role: 'doctor' | 'receptionist' | 'admin';
    message?: string;
  }): Promise<{ message: string; invitationId: string }> => {
    const response = await axiosInstance.post<{ message: string; invitationId: string }>(
      '/clinic/staff/invite',
      { email: data.email, role: data.role, message: data.message }
    );
    return response.data;
  },

  /**
   * Update staff member role or status
   */
  updateStaff: async (
    userId: string,
    data: {
      role?: 'doctor' | 'receptionist' | 'admin';
      status?: ClinicStaffStatus;
    }
  ): Promise<ClinicStaffAssociation> => {
    const response = await axiosInstance.put<ClinicStaffAssociation>(
      `/clinic/staff/${userId}`,
      data
    );
    return response.data;
  },

  /**
   * Remove staff member from clinic
   */
  removeStaff: async (userId: string, reason?: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete<{ message: string }>(`/clinic/staff/${userId}`, {
      data: { reason },
    });
    return response.data;
  },

  // Staff Management Endpoints

  /**
   * Create new staff member
   */
  createStaff: async (data: {
    email: string;
    role: 'doctor' | 'receptionist' | 'admin';
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Promise<ClinicStaffAssociation> => {
    const response = await axiosInstance.post<ClinicStaffAssociation>(CLINIC_ENDPOINTS.STAFF_CREATE, data);
    return response.data;
  },

  /**
   * Create new doctor (creates Staff + User) using Step 1 payload
   * POST /staff/doctor
   * Response structure: { staff: { _id: string, ... } }
   */
  createDoctor: async (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    dob: string;
    gender: string;
    npiNumber?: string;
    basicInfo?: any;
    basicProfessionalInfo?: any;
    address?: any;
    profilePhoto?: any;
  }): Promise<{ staff: any }> => {
    const response = await axiosInstance.post<{ staff: any }>(CLINIC_ENDPOINTS.STAFF_CREATE_DOCTOR, data);
    return response.data;
  },


  /**
   * Create new receptionist (creates Staff + User)
   * POST /staff/receptionist
   * Response structure: { staff: { _id: string, ... } }
   * Payload structure:
   * {
   *   firstName, lastName, email, phone, password, dob, gender (top level - required for validation)
   *   isPublishable, providerType, displayName (top level)
   *   basicInfo: {
   *     firstName, lastName, email, phone, dob, gender,
   *     profilePhoto: { cdnUrl, baseUrl, s3Url },
   *     address: { street, city, state, zip }
   *   }
   * }
   */
  createReceptionist: async (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    dob: string;
    gender: string;
    isPublishable?: boolean;
    providerType?: string;
    displayName?: string;
    basicInfo: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dob: string;
      gender: string;
      profilePhoto?: {
        cdnUrl?: string;
        baseUrl?: string;
        s3Url?: string;
      };
      address?: {
        street?: string;
        city?: string;
        state?: string;
        zip?: string;
      };
    };
  }): Promise<{ staff: any }> => {
    const response = await axiosInstance.post<{ staff: any }>(CLINIC_ENDPOINTS.STAFF_CREATE_RECEPTIONIST, data);
    return response.data;
  },

  /**
   * Get staff by ID
   * GET /staff/{id}
   * Returns the same structure as GET /staff but for a single staff member
   */
  getStaffById: async (id: string): Promise<any> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_GET_BY_ID.replace('{id}', id);
    const response = await axiosInstance.get<any>(endpoint);
    return response.data;
  },

  /**
   * Update staff status
   */
  updateStaffStatus: async (id: string, status: ClinicStaffStatus): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_UPDATE_STATUS.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, { status });
    return response.data;
  },

  // Staff Onboarding - Doctor

  /**
   * Update doctor basic info (Step 1)
   * PUT /staff/onboarding/{id}/doctor/basic-info
   */
  updateDoctorBasicInfo: async (
    id: string, // Clinic ID
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_DOCTOR_BASIC_INFO.replace('{id}', id);
    console.log(`[clinicApi.updateDoctorBasicInfo] Calling PUT ${endpoint}`);
    console.log(`[clinicApi.updateDoctorBasicInfo] Clinic ID: ${id}`);
    console.log(`[clinicApi.updateDoctorBasicInfo] Payload keys:`, Object.keys(data || {}));
    console.log(`[clinicApi.updateDoctorBasicInfo] Full payload:`, JSON.stringify(data, null, 2));
    
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    console.log(`[clinicApi.updateDoctorBasicInfo] ✅ PUT request successful! Status: ${response.status}`);
    return response.data;
  },

  /**
   * Update doctor credentials (Step 2)
   */
  updateDoctorCredentials: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_DOCTOR_CREDENTIALS.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    return response.data;
  },

  /**
   * Update doctor services (Step 3)
   */
  updateDoctorServices: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_DOCTOR_SERVICES.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    return response.data;
  },

  /**
   * Update doctor pricing (Step 4)
   */
  updateDoctorPricing: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_DOCTOR_PRICING.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    return response.data;
  },

  /**
   * Update doctor availability (Step 5)
   */
  updateDoctorAvailability: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_DOCTOR_AVAILABILITY.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    return response.data;
  },

  /**
   * Update doctor payment info (Step 6)
   */
  updateDoctorPayment: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_DOCTOR_PAYMENT.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    return response.data;
  },

  /**
   * Submit doctor profile (Step 7)
   */
  submitDoctorProfile: async (
    id: string,
    data?: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_DOCTOR_SUBMIT.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data || {});
    return response.data;
  },


  // Staff Onboarding - Receptionist

  /**
   * Update receptionist basic info (Step 1)
   */
  updateReceptionistBasicInfo: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_RECEPTION_BASIC_INFO.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    return response.data;
  },

  /**
   * Update receptionist credentials (Step 2)
   * PUT /staff/onboarding/{id}/reception/credentials
   */
  updateReceptionistCredentials: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_RECEPTION_CREDENTIALS.replace('{id}', id);
    console.log(`[clinicApi.updateReceptionistCredentials] Calling PUT ${endpoint}`);
    console.log(`[clinicApi.updateReceptionistCredentials] Staff ID: ${id}`);
    console.log(`[clinicApi.updateReceptionistCredentials] Payload:`, JSON.stringify(data, null, 2));
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    console.log(`[clinicApi.updateReceptionistCredentials] ✅ PUT request successful! Status: ${response.status}`);
    return response.data;
  },

  /**
   * Update receptionist schedule (Step 3)
   */
  updateReceptionistSchedule: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_RECEPTION_SCHEDULE.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    return response.data;
  },

  /**
   * Update receptionist permissions (Step 4)
   */
  updateReceptionistPermissions: async (
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_RECEPTION_PERMISSIONS.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data);
    return response.data;
  },

  /**
   * Submit receptionist profile (Step 5)
   */
  submitReceptionistProfile: async (
    id: string,
    data?: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> => {
    const endpoint = CLINIC_ENDPOINTS.STAFF_ONBOARDING_RECEPTION_SUBMIT.replace('{id}', id);
    const response = await axiosInstance.put<ClinicStaffAssociation>(endpoint, data || {});
    return response.data;
  },
};

export default clinicApi;
